import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { properties } from "@/data/properties";

// Calculate property counts dynamically from actual data
const getPropertyCountByLocation = (searchTerms: string[]) => {
  return properties.filter(p => 
    searchTerms.some(term => p.location.toLowerCase().includes(term.toLowerCase()))
  ).length;
};

const destinations = [
  {
    name: "Italy",
    region: "Amalfi Coast & Sardinia",
    properties: getPropertyCountByLocation(["Italy", "Amalfi", "Sardinia"]),
    description: "From cliffside villas overlooking Positano to Costa Smeralda estates of distinction.",
  },
  {
    name: "Spain",
    region: "Marbella & Costa del Sol",
    properties: getPropertyCountByLocation(["Spain", "Marbella"]),
    description: "Mediterranean elegance meets contemporary luxury in Costa del Sol.",
  },
  {
    name: "Monaco",
    region: "Monte Carlo",
    properties: getPropertyCountByLocation(["Monaco", "Monte Carlo"]),
    description: "The pinnacle of European luxury living with yacht-filled harbors.",
  },
  {
    name: "Dubai",
    region: "Palm Jumeirah & Marina",
    properties: getPropertyCountByLocation(["Dubai", "UAE"]),
    description: "Architectural marvels and ultra-modern penthouses in the desert oasis.",
  },
];

export const DestinationsSection = () => {
  return (
    <section id="destinations" className="py-24 lg:py-32 bg-secondary">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-gold mb-4">
            Global Presence
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground max-w-2xl">
            Markets We
            <span className="block italic font-medium text-primary">Know Intimately</span>
          </h2>
        </motion.div>

        {/* Destinations List */}
        <div className="space-y-0">
          {destinations.map((destination, index) => (
            <motion.div
              key={destination.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
            <Link
              to={`/properties?location=${encodeURIComponent(destination.name)}`}
              className="group block py-8 lg:py-10 border-t border-border last:border-b hover:bg-secondary/50 transition-colors duration-300 -mx-6 px-6 lg:-mx-12 lg:px-12"
            >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-8">
                  {/* Left: Name and Arrow */}
                  <div className="flex items-center gap-4 lg:gap-8 lg:w-1/3">
                    <span className="font-body text-sm text-muted-foreground w-8">
                      0{index + 1}
                    </span>
                    <h3 className="font-display text-3xl lg:text-4xl xl:text-5xl text-foreground group-hover:text-gold transition-colors duration-300">
                      {destination.name}
                    </h3>
                    <motion.div
                      initial={{ x: 0, y: 0 }}
                      whileHover={{ x: 4, y: -4 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowUpRight
                        size={24}
                        className="text-muted-foreground group-hover:text-gold transition-colors duration-300"
                      />
                    </motion.div>
                  </div>

                  {/* Center: Region and Description */}
                  <div className="lg:w-1/3 pl-12 lg:pl-0">
                    <p className="font-body text-sm uppercase tracking-[0.15em] text-gold mb-2">
                      {destination.region}
                    </p>
                    <p className="font-body text-muted-foreground text-sm lg:text-base leading-relaxed group-hover:text-foreground transition-colors duration-300">
                      {destination.description}
                    </p>
                  </div>

                  {/* Right: Properties Count */}
                  <div className="pl-12 lg:pl-0 lg:text-right lg:w-1/3">
                    <span className="font-display text-4xl lg:text-5xl text-foreground group-hover:text-gold transition-colors duration-300">
                      {destination.properties}
                    </span>
                    <span className="font-body text-sm text-muted-foreground ml-2">
                      properties
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
